import React, { useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MapPin } from 'lucide-react';
import { Modal, Select, InputNumber, Button, Space, Typography, Spin, message } from 'antd';
import { AimOutlined, SearchOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import eventService from '../../services/eventService';
import { getCurrentLocation } from '../../utils/locationUtils';

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_CENTER = [-29.0, 25.0];

// Lucide's standard outlined MapPin in the active primary colour.
const buildPinIcon = () =>
  L.divIcon({
    className: 'event-location-pin',
    html: renderToStaticMarkup(
      <MapPin
        className="event-location-pin__icon"
        size={40}
        fill="var(--primary-color, #C81010)"
        color="#fff"
      />
    ),
    iconSize: [40, 40],
    iconAnchor: [20, 35],
  });

// Moves the map view when the chosen position changes (search, device, manual).
const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 14));
  }, [map, position]);
  return null;
};

// The map initialises before the modal finishes opening, so Leaflet measures the
// wrong container size and only loads a few tiles. Recompute the size once the
// modal transition has settled (and whenever the window resizes).
const InvalidateSize = () => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 300);
    const onResize = () => map.invalidateSize();
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [map]);
  return null;
};

// Dropping the pin anywhere on the map selects that point.
const ClickToPlace = ({ onPick }) => {
  useMapEvents({
    click: (event) => onPick(event.latlng.lat, event.latlng.lng),
  });
  return null;
};

/**
 * Picks an event's GPS location by address search, map click/drag, manual
 * coordinates, or the device's current location. Returns { latitude, longitude,
 * address } to the caller on save. Uses the same Leaflet/OSM map as the portal.
 */
const EventLocationPickerModal = ({ open, onClose, initial, onSave }) => {
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [address, setAddress] = useState('');
  const [addressOptions, setAddressOptions] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [locating, setLocating] = useState(false);
  const [mapView, setMapView] = useState('map');

  const pinIcon = useMemo(() => buildPinIcon(), []);

  // Seed from the current value each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setLat(Number.isFinite(initial?.latitude) ? initial.latitude : null);
    setLng(Number.isFinite(initial?.longitude) ? initial.longitude : null);
    setAddress(initial?.address ?? '');
    setAddressOptions([]);
  }, [open, initial]);

  const hasPosition = Number.isFinite(lat) && Number.isFinite(lng);
  const position = hasPosition ? [lat, lng] : null;

  const fetchAddress = async (nextLat, nextLng) => {
    try {
      const result = await eventService.reverseGeocode(nextLat, nextLng);
      return result?.custom_formatted_address ?? '';
    } catch (error) {
      console.warn('Reverse geocode failed:', error);
      return '';
    }
  };

  const searchAddresses = async (term) => {
    if (!term || term.length < 3) {
      setAddressOptions([]);
      return;
    }
    setLoadingAddresses(true);
    try {
      const suggestions = await eventService.autocompleteAddress(term);
      const options = (suggestions ?? [])
        .map((suggestion) => suggestion.placePrediction)
        .filter(Boolean)
        .slice(0, 6)
        .map((prediction) => ({ id: prediction.placeId, title: prediction.text?.text ?? '' }));
      setAddressOptions(options);
    } catch (error) {
      console.warn('Address search failed:', error);
      setAddressOptions([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleAddressSelect = async (placeId) => {
    try {
      const result = await eventService.geocodePlace(placeId);
      const location = result?.geometry?.location;
      if (location) {
        setLat(location.lat);
        setLng(location.lng);
        setAddress(result.custom_formatted_address ?? '');
      }
    } catch (error) {
      console.warn('Geocode by place id failed:', error);
    }
  };

  // Moving the pin sets the coordinates and resolves the address from them.
  const setPosition = async (nextLat, nextLng) => {
    setLat(nextLat);
    setLng(nextLng);
    const resolved = await fetchAddress(nextLat, nextLng);
    if (resolved) setAddress(resolved);
  };

  const useDeviceLocation = async () => {
    setLocating(true);
    try {
      const device = await getCurrentLocation();
      if (device && Number.isFinite(device.latitude) && Number.isFinite(device.longitude)) {
        setPosition(device.latitude, device.longitude);
      } else {
        message.error('Could not get your current location.');
      }
    } catch {
      message.error('Could not get your current location.');
    } finally {
      setLocating(false);
    }
  };

  const handleSave = async () => {
    if (!hasPosition) {
      message.error('Set a location before saving.');
      return;
    }
    // Make sure the address is resolved even if the pin was just dropped or typed.
    let finalAddress = address;
    if (!finalAddress) {
      finalAddress = await fetchAddress(lat, lng);
      if (finalAddress) setAddress(finalAddress);
    }
    onSave?.({ latitude: lat, longitude: lng, address: finalAddress });
    onClose?.();
  };

  return (
    <Modal
      title="Set Event Location"
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={window.innerWidth < 768 ? '95%' : 640}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" disabled={!hasPosition} onClick={handleSave}>
            Save Location
          </Button>
        </Space>
      }
    >
      <style>
        {`
          .event-location-pin__icon {
            display: block;
            filter: drop-shadow(0 2px 4px rgba(15, 23, 42, 0.35));
          }
        `}
      </style>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Select
          showSearch
          filterOption={false}
          style={{ width: '100%' }}
          placeholder="Search for an address"
          loading={loadingAddresses}
          onSearch={searchAddresses}
          onSelect={handleAddressSelect}
          notFoundContent={loadingAddresses ? <Spin size="small" /> : null}
          suffixIcon={<SearchOutlined />}
          value={undefined}
        >
          {addressOptions.map((option) => (
            <Option key={option.id} value={option.id}>
              {option.title}
            </Option>
          ))}
        </Select>

        {/* Same map/satellite switch style the PWA map page uses. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            style={{
              display: 'inline-flex',
              gap: 8,
              background: 'white',
              padding: 4,
              borderRadius: 4,
              border: '1px solid #d9d9d9',
            }}
          >
            {[
              { label: 'Map', value: 'map' },
              { label: 'Satellite', value: 'satellite' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setMapView(option.value)}
                style={{
                  padding: '6px 14px',
                  fontSize: 14,
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: 3,
                  outline: 'none',
                  color: 'black',
                  backgroundColor: mapView === option.value ? '#e0e0e0' : 'white',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ width: '100%', height: 300, borderRadius: 8, overflow: 'hidden' }}>
          <MapContainer
            center={position ?? DEFAULT_CENTER}
            zoom={hasPosition ? 14 : 5}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
          >
            {mapView === 'satellite' ? (
              <>
                <TileLayer
                  noWrap
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                />
                <TileLayer
                  noWrap
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                  attribution="&copy; Esri"
                />
                <TileLayer
                  noWrap
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  attribution="&copy; Esri"
                />
              </>
            ) : (
              <TileLayer
                noWrap
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            )}
            <ClickToPlace onPick={setPosition} />
            <Recenter position={position} />
            <InvalidateSize />
            {hasPosition && (
              <Marker
                position={position}
                icon={pinIcon}
                draggable
                eventHandlers={{
                  dragend: (event) => {
                    const { lat: nextLat, lng: nextLng } = event.target.getLatLng();
                    setPosition(nextLat, nextLng);
                  },
                }}
              />
            )}
          </MapContainer>
        </div>

        <Space wrap style={{ width: '100%' }}>
          <InputNumber
            placeholder="Latitude"
            style={{ width: 160 }}
            step={0.000001}
            precision={6}
            value={lat}
            onChange={(value) => setLat(Number.isFinite(value) ? value : null)}
          />
          <InputNumber
            placeholder="Longitude"
            style={{ width: 160 }}
            step={0.000001}
            precision={6}
            value={lng}
            onChange={(value) => setLng(Number.isFinite(value) ? value : null)}
          />
          <Button icon={<AimOutlined />} loading={locating} onClick={useDeviceLocation}>
            Use My Location
          </Button>
        </Space>

        <Text type="secondary">{address || 'No address selected yet.'}</Text>
      </Space>
    </Modal>
  );
};

export default EventLocationPickerModal;
